import { Link } from 'react-router-dom';

import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'emergency' | 'inverse';
type Size = 'sm' | 'md' | 'lg';

interface CommonProps {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: React.ReactNode;
}

type ButtonAsButton = CommonProps & {
  as?: 'button';
  type?: 'button' | 'submit';
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel?: string;
};

type ButtonAsLink = CommonProps & {
  as: 'link';
  to: string;
  ariaLabel?: string;
};

type ButtonAsAnchor = CommonProps & {
  as: 'anchor';
  href: string;
  target?: '_blank' | '_self';
  rel?: string;
  ariaLabel?: string;
};

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

const baseStyles =
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-leaf disabled:cursor-not-allowed disabled:opacity-60';

const sizeStyles: Record<Size, string> = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-5 py-3 text-base',
  lg: 'px-6 py-4 text-lg',
};

const variantStyles: Record<Variant, string> = {
  primary: 'bg-bark text-paper hover:bg-moss',
  secondary: 'bg-paper text-bark border border-stone hover:border-bark',
  inverse: 'bg-paper! text-bark! hover:bg-stone!',
  ghost: 'text-bark hover:bg-stone/40',
  emergency: 'bg-emergency text-paper hover:brightness-110',
};

export function Button(props: ButtonProps): React.ReactNode {
  const variant = props.variant ?? 'primary';
  const size = props.size ?? 'md';
  const classes = cn(baseStyles, sizeStyles[size], variantStyles[variant], props.className);

  if (props.as === 'link') {
    return (
      <Link to={props.to} className={classes} aria-label={props.ariaLabel}>
        {props.children}
      </Link>
    );
  }
  if (props.as === 'anchor') {
    return (
      <a
        href={props.href}
        target={props.target}
        rel={props.rel}
        className={classes}
        aria-label={props.ariaLabel}
      >
        {props.children}
      </a>
    );
  }
  return (
    <button
      type={props.type ?? 'button'}
      onClick={props.onClick}
      disabled={props.disabled}
      className={classes}
      aria-label={props.ariaLabel}
    >
      {props.children}
    </button>
  );
}
